import { CountryInfo } from 'pages/insights-pro/country-tab/model';
import { FormattedMessage } from 'react-intl';
import { TileContent } from 'components/Layout/PageContainer';
import { Flag } from 'components/Flag';
import { Measure } from 'services/model/model.service';
import { useCallback, useMemo } from 'react';

interface ActiveCountriesProps {
  countries: CountryInfo[];
  isComparisonActive: boolean;
  qtyMeasure: Measure;
}

interface StatusRowProps {
  defaultLabel: string;
  countries: CountryInfo[];
  translationKey: string;
}

function StatusRow({ defaultLabel, countries, translationKey }: StatusRowProps) {
  return (
    <>
      <div className="flex items-center">
        <span className="text-md mr-2 font-bold min-w-[120px]">
          <FormattedMessage id={translationKey} defaultMessage={defaultLabel} />
        </span>
        <span className="px-2 py-1 bg-gray-100 rounded-full text-sm font-bold">{countries.length}</span>
      </div>
      <div>
        {countries.map(country => (
          <Flag key={country.code} countryCode={country.code} tooltip={country.name} className="mr-1 inline-block" />
        ))}
      </div>
    </>
  );
}

export function ActiveCountries({ countries, isComparisonActive, qtyMeasure }: ActiveCountriesProps) {
  const getFilteredCountries = useCallback(
    (filter: (value: number, comparisonValue: number) => boolean) =>
      countries.filter(country =>
        filter(qtyMeasure.getValue(country.entry.values), qtyMeasure.getValue(country.entry.compareValues))
      ),
    [countries, qtyMeasure]
  );

  const activeCountries = useMemo(() => getFilteredCountries(value => value > 0).length, [getFilteredCountries]);

  const launchedCountries = useMemo(
    () => getFilteredCountries((value, compareValue) => value > 0 && compareValue === 0),
    [getFilteredCountries]
  );

  const discontinuedCountries = useMemo(
    () => getFilteredCountries((value, compareValue) => value === 0 && compareValue > 0),
    [getFilteredCountries]
  );

  return (
    <TileContent className="flex items-center">
      <div className="flex w-full">
        <div className="flex items-center justify-center w-2/5 text-4xl font-bold pr-6">{activeCountries}</div>

        <div className="w-px bg-gray-200 min-h-full" />

        {isComparisonActive && (
          <div className="flex flex-col gap-6 pl-10 w-3/5">
            <div>
              <StatusRow
                defaultLabel="Launched"
                countries={launchedCountries}
                translationKey="insights.country_tab.active_countries_header.countries_launched"
              />
            </div>

            <div>
              <StatusRow
                defaultLabel="Discontinued"
                countries={discontinuedCountries}
                translationKey="insights.country_tab.active_countries_header.countries_discontinued"
              />
            </div>
          </div>
        )}
      </div>
    </TileContent>
  );
}
